<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CompanySortMenu from './components/CompanySortMenu.vue';

// Companies header, same anatomy as the contacts one (CrmB): title + total,
// 300px search, "⋮" and the primary "Nueva empresa".
const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  totalCount: { type: Number, default: 0 },
  activeSort: { type: String, default: 'name' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['search', 'update:sort', 'add']);

const { t, n } = useI18n();

const totalLabel = computed(() =>
  t('COMPANIES.TOTAL_COUNT', { count: n(Number(props.totalCount || 0)) })
);
</script>

<template>
  <header
    class="sticky top-0 z-10 flex items-center gap-3 px-5 py-4 bg-n-solid-2 border-b border-n-weak"
  >
    <h1 class="m-0 text-lg font-semibold truncate text-n-slate-12">
      {{ headerTitle }}
    </h1>
    <span class="text-[12.5px] text-n-slate-10 truncate">{{ totalLabel }}</span>

    <div class="flex items-center gap-2 ml-auto flex-shrink-0">
      <label
        class="relative flex items-center w-[300px] max-w-[40vw] h-[34px] rounded-[7px] bg-n-alpha-2 text-n-slate-10 focus-within:bg-n-alpha-1 focus-within:ring-1 focus-within:ring-n-brand/50 transition-colors"
      >
        <Icon icon="i-lucide-search" class="absolute ltr:left-3 rtl:right-3 size-[15px]" />
        <input
          :value="searchValue"
          type="search"
          :placeholder="t('COMPANIES.SEARCH_PLACEHOLDER')"
          class="w-full h-full bg-transparent border-0 outline-none reset-base text-[13px] text-n-slate-12 placeholder:text-n-slate-10 ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3"
          @input="emit('search', $event.target.value)"
        />
      </label>
      <CompanySortMenu
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        @update:sort="emit('update:sort', $event)"
      />
      <Button
        icon="i-lucide-plus"
        size="sm"
        class="h-[34px] rounded-[7px]"
        :label="t('COMPANIES.NEW_COMPANY')"
        @click="emit('add')"
      />
    </div>
  </header>
</template>
